// M17 P5 — Recruiter dashboard redesign: pipeline counters (clickable) + KPI row.
// "Status not updated" = Going entries with a past date and no "Interview
// happened" recorded, excluding Backed Out — exactly per spec.
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  AlertTriangle, ArrowRight, BriefcaseBusiness, CalendarClock, CalendarDays,
  CheckCircle2, ClipboardCheck, Inbox, PhoneCall, Play, Share2, Target, UserPlus, UserRound,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { api, getErrorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const Counter = ({ to, icon: Icon, label, value, tone, testId }) => (
  <Link to={to} data-testid={testId} className="group">
    <Card className="rounded-xl border-border shadow-none transition-colors group-hover:border-primary/50">
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <span className={`h-9 w-9 rounded-lg flex items-center justify-center ${tone}`}>
            <Icon className="h-4.5 w-4.5" />
          </span>
          <ArrowRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
        </div>
        <p className="text-2xl font-heading font-semibold mt-3">{value}</p>
        <p className="text-xs text-muted-foreground mt-0.5">{label}</p>
      </CardContent>
    </Card>
  </Link>
);

export default function RecruiterDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [daily, setDaily] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/recruiter-flow/dashboard-summary")
      .then((r) => setData(r.data))
      .catch((err) => toast.error(getErrorMessage(err)))
      .finally(() => setLoading(false));
    api.get("/dashboard/daily-activity").then((r) => setDaily(r.data.counts)).catch(() => {});
  }, []);

  if (loading || !data) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-72 rounded-lg" />
        <div className="grid sm:grid-cols-3 gap-4">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-28 rounded-xl" />)}</div>
        <div className="grid sm:grid-cols-3 gap-4">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-28 rounded-xl" />)}</div>
      </div>
    );
  }

  const { counters: c, kpis: k, active_slot: slot } = data;
  const pct = k.target > 0 ? Math.min(Math.round((c.joined_this_month / k.target) * 100), 100) : 0;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl sm:text-3xl font-heading font-semibold tracking-tight">
          Hello, {user.name?.split(" ")[0]}
        </h1>
        <p className="text-muted-foreground mt-1 text-sm">Your pipeline at a glance — tap any counter to jump in.</p>
      </div>

      {/* current working slot */}
      <Card className="rounded-xl border-primary/30 shadow-none" data-testid="dashboard-slot-card">
        <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center gap-3">
          <span className="h-9 w-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
            <UserRound className="h-4.5 w-4.5" />
          </span>
          {slot ? (
            <>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium">Currently working on: {slot.candidate_name}</p>
                <p className="text-xs text-muted-foreground">
                  {slot.app_code} · {slot.status} — take one of the four actions to free your slot
                </p>
              </div>
              <Link to={`/recruiter/applications/${slot.application_id}`}
                    className="text-sm text-primary hover:underline inline-flex items-center gap-1 shrink-0"
                    data-testid="dashboard-slot-link">
                Open <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </>
          ) : (
            <>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium">Your working slot is free</p>
                <p className="text-xs text-muted-foreground">Pick your next candidate from Fresh Leads or the Candidate Directory.</p>
              </div>
              <Link to="/recruiter/unassigned" className="text-sm text-primary hover:underline inline-flex items-center gap-1 shrink-0"
                    data-testid="dashboard-slot-free-link">
                <Play className="h-3.5 w-3.5" /> Fresh Leads
              </Link>
            </>
          )}
        </CardContent>
      </Card>

      {/* today's activity */}
      {daily && (
        <div data-testid="daily-activity-section">
          <h2 className="text-base font-heading font-semibold mb-3">Today's activity</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {[
              { icon: UserPlus, label: "Leads claimed today", value: daily.claimed_today, tone: "bg-primary/10 text-primary", tid: "daily-claimed" },
              { icon: CalendarClock, label: "Scheduled today", value: daily.scheduled_today, tone: "bg-violet-50 text-violet-600", tid: "daily-scheduled" },
              { icon: PhoneCall, label: "Pending call-backs", value: daily.pending_callbacks, tone: "bg-amber-50 text-amber-600", tid: "daily-callbacks" },
              { icon: Share2, label: "Leads forwarded today", value: daily.forwarded_today, tone: "bg-sky-50 text-sky-600", tid: "daily-forwarded" },
            ].map((s) => (
              <Card key={s.tid} className="rounded-xl border-border shadow-none" data-testid={s.tid}>
                <CardContent className="p-4">
                  <span className={`h-9 w-9 rounded-lg flex items-center justify-center ${s.tone}`}>
                    <s.icon className="h-4.5 w-4.5" />
                  </span>
                  <p className="text-2xl font-heading font-semibold mt-3">{s.value}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{s.label}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* pipeline counters */}
      <div>
        <h2 className="text-base font-heading font-semibold mb-3">My pipeline</h2>
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
          <Counter to="/recruiter/scheduled" icon={CalendarClock} label="Today's lineup"
                   value={c.todays_lineup} tone="bg-violet-50 text-violet-600" testId="counter-todays-lineup" />
          <Counter to="/recruiter/lineup-board" icon={CalendarDays} label="Going"
                   value={c.going} tone="bg-emerald-50 text-emerald-600" testId="counter-going" />
          <Counter to="/recruiter/selection" icon={ClipboardCheck} label="Selection (awaiting admin)"
                   value={c.selection_backlog} tone="bg-sky-50 text-sky-600" testId="counter-selection" />
          <Counter to="/recruiter/employment" icon={CheckCircle2} label="Joined this month (Premium)"
                   value={c.joined_this_month} tone="bg-emerald-600/10 text-emerald-700" testId="counter-joined" />
          <Counter to="/recruiter/employment" icon={BriefcaseBusiness} label="Joining Confirmation"
                   value={c.joining_confirmation} tone="bg-amber-50 text-amber-600" testId="counter-joining-confirmation" />
        </div>
      </div>

      {/* KPI row */}
      <div>
        <h2 className="text-base font-heading font-semibold mb-3">This month</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <Card className="rounded-xl border-border shadow-none" data-testid="kpi-target-card">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Target className="h-3.5 w-3.5" /> Target (Premium)
              </div>
              <p className="text-2xl font-heading font-semibold mt-2">
                {k.target > 0 ? `${c.joined_this_month} / ${k.target}` : "—"}
              </p>
              {k.target > 0 ? (
                <Progress value={pct} className="h-1.5 mt-2" />
              ) : (
                <p className="text-xs text-muted-foreground mt-1">No target set yet — ask your admin.</p>
              )}
            </CardContent>
          </Card>
          <Card className="rounded-xl border-border shadow-none" data-testid="kpi-gap-card">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <CheckCircle2 className="h-3.5 w-3.5" /> Gap to target
              </div>
              <p className={`text-2xl font-heading font-semibold mt-2 ${k.target > 0 && k.gap === 0 ? "text-emerald-600" : ""}`}>
                {k.target > 0 ? k.gap : "—"}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                {k.target > 0 ? (k.gap === 0 ? "Target achieved — great work!" : `${k.gap} more Premium joining${k.gap > 1 ? "s" : ""} to go`) : "Shown once a target is set."}
              </p>
            </CardContent>
          </Card>
          <Card className="rounded-xl border-border shadow-none" data-testid="kpi-all-joinings-card">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <BriefcaseBusiness className="h-3.5 w-3.5" /> All joinings this month
              </div>
              <p className="text-2xl font-heading font-semibold mt-2">{k.total_joined_month ?? 0}</p>
              <p className="text-xs text-muted-foreground mt-1">Across all companies — Premium and Non-premium.</p>
            </CardContent>
          </Card>
          <Card className={`rounded-xl shadow-none ${k.status_not_updated > 0 ? "border-amber-300 bg-amber-50/40" : "border-border"}`}
                data-testid="kpi-status-not-updated-card">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <AlertTriangle className={`h-3.5 w-3.5 ${k.status_not_updated > 0 ? "text-amber-600" : ""}`} /> Status not updated
              </div>
              <p className={`text-2xl font-heading font-semibold mt-2 ${k.status_not_updated > 0 ? "text-amber-700" : ""}`}>
                {k.status_not_updated}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                {k.status_not_updated > 0 ? (
                  <>Past Going entries with no “Interview happened” — <Link to="/recruiter/lineup-board" className="text-primary hover:underline">update them</Link></>
                ) : "All past lineups are up to date."}
              </p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* quick links */}
      <div className="flex flex-wrap gap-2">
        <Link to="/recruiter/unassigned" data-testid="quick-fresh-leads">
          <Badge variant="outline" className="rounded-full px-3 py-1.5 hover:border-primary/60 cursor-pointer">
            <Inbox className="h-3.5 w-3.5 mr-1.5" /> Fresh Leads
          </Badge>
        </Link>
        <Link to="/recruiter/candidates" data-testid="quick-directory">
          <Badge variant="outline" className="rounded-full px-3 py-1.5 hover:border-primary/60 cursor-pointer">
            <UserRound className="h-3.5 w-3.5 mr-1.5" /> Candidate Directory
          </Badge>
        </Link>
        <Link to="/recruiter/profile" data-testid="quick-profile">
          <Badge variant="outline" className="rounded-full px-3 py-1.5 hover:border-primary/60 cursor-pointer">
            <Target className="h-3.5 w-3.5 mr-1.5" /> My Profile & Target
          </Badge>
        </Link>
      </div>
    </div>
  );
}
